import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import moment from 'moment';
import { Clock, MapPin, Star } from 'lucide-react';
import UserStatusBadges from 'components/Badges/UserStatusBadges/UserStatusBadges';
import TalentPoolHeart from 'components/TalentPoolHeart/TalentPoolHeart';
import {
  browseHitHasMusicalRole,
  browseProjectsFromHit,
  UserBrowseProjectsList,
} from 'components/Browse/UserBrowseProjectCard/UserBrowseProjectCard';
import {
  browseWorkExperienceFromHit,
  UserBrowseWorkExperienceList,
} from 'components/Browse/UserBrowseWorkExperienceList/UserBrowseWorkExperienceList';
import styles from './UserBrowseCard.module.scss';

export const MAX_ROLES = 3;
export const MAX_SKILLS_DESKTOP = 6;
/** Matches UserBrowseCard.module.scss / Bootstrap $screen-xs-max. */
export const SKILLS_CAP_BREAKPOINT_PX = 767;
export const MAX_SKILLS_MOBILE = 3;
export const ACTIVE_NOW_MINUTES = 15;

export function maxSkillsForViewport() {
  if (typeof window === 'undefined') {
    return MAX_SKILLS_DESKTOP;
  }
  return window.innerWidth <= SKILLS_CAP_BREAKPOINT_PX
    ? MAX_SKILLS_MOBILE
    : MAX_SKILLS_DESKTOP;
}

const HIT_CARD_ICON_SIZE = 14;

const iconPin = renderToStaticMarkup(
  <MapPin
    className={styles.iconMeta}
    size={HIT_CARD_ICON_SIZE}
    aria-hidden={true}
  />
);

const iconStar = renderToStaticMarkup(
  <Star
    className={styles.iconStar}
    size={HIT_CARD_ICON_SIZE}
    aria-hidden={true}
    fill="currentColor"
    strokeWidth={0}
  />
);

const iconClock = renderToStaticMarkup(
  <Clock
    className={styles.iconMeta}
    size={HIT_CARD_ICON_SIZE}
    aria-hidden={true}
  />
);

function escapeHtml(str) {
  if (str == null || str === '') {
    return '';
  }
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function profilePath(username) {
  if (username == null || String(username).trim() === '') {
    return '#';
  }
  return `/${encodeURIComponent(String(username))}`;
}

/**
 * Typesense / InstantSearch user document id for API calls (invite, etc.).
 *
 * @param {Record<string, unknown>} hit
 * @returns {string}
 */
export function freelancerIdFromHit(hit) {
  if (!hit || typeof hit !== 'object') {
    return '';
  }
  const raw =
    hit.id != null ? hit.id : hit.objectID != null ? hit.objectID : hit.user_id;
  if (raw == null || raw === '') {
    return '';
  }
  return String(raw);
}

/**
 * Talent pool UI state: prefer Redux when loaded for this username, otherwise Typesense `followers`.
 *
 * @param {Record<string, unknown>} hit
 * @param {string|number|null|undefined} viewerUserId
 * @param {Record<string, { isInTalentPool?: boolean }>|undefined|null} portfolio
 */
function talentPoolDisplayForHit(hit, viewerUserId, portfolio) {
  const username =
    hit && hit.username != null ? String(hit.username).trim() : '';
  if (!username) {
    return false;
  }
  const viewerKey =
    viewerUserId != null && viewerUserId !== '' ? String(viewerUserId) : null;
  if (viewerKey) {
    const slice = portfolio && portfolio[username];
    if (slice && typeof slice.isInTalentPool === 'boolean') {
      return slice.isInTalentPool;
    }
  } else {
    return false;
  }
  const followers = hit.followers;
  return (
    Array.isArray(followers) && followers.some((id) => String(id) === viewerKey)
  );
}

function locationText(hit) {
  const loc = hit.location;
  if (!loc || typeof loc !== 'object') {
    return '';
  }
  if (loc.text && String(loc.text).trim()) {
    return String(loc.text).trim();
  }
  const city = loc.city && String(loc.city).trim();
  const country = loc.country && String(loc.country).trim();
  return [city, country].filter(Boolean).join(', ');
}

function initialsFromDisplayName(name) {
  if (!name || !String(name).trim()) {
    return '?';
  }
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return String(name).substring(0, 2).toUpperCase();
}

function normalizeTimestamp(raw) {
  if (raw == null || raw === '') {
    return null;
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return null;
  }
  return n > 1e12 ? n / 1000 : n;
}

function lastActiveLabel(ts) {
  const sec = normalizeTimestamp(ts);
  if (sec == null) {
    return null;
  }
  const m = moment.unix(sec);
  if (!m.isValid()) {
    return null;
  }
  const minutesAgo = moment().diff(m, 'minutes');
  if (minutesAgo >= 0 && minutesAgo < ACTIVE_NOW_MINUTES) {
    return 'Active now';
  }
  if (moment().diff(m, 'months') >= 1) {
    return null;
  }
  return `Last active ${m.fromNow()}`;
}

function statsRowHtml(hit) {
  const total = Number(hit.total_ratings);
  const hasRatings = Number.isFinite(total) && total >= 1;
  const rawRating = Number(hit.rating);
  const rating = Number.isFinite(rawRating) ? rawRating : 0;
  const ratingStr = rating.toFixed(1);

  const ratingPart = `<span class="${
    styles.ratingInline
  }">${iconStar}<strong class="${styles.statValue}">${escapeHtml(
    ratingStr
  )}</strong></span>`;

  const countBadge = hasRatings
    ? `<span class="${styles.ratingsCountBadge}">${escapeHtml(
        String(total)
      )} rating${total === 1 ? '' : 's'}</span>`
    : '';

  const active = lastActiveLabel(hit.last_active);
  const activePart = active
    ? `<span class="${styles.activeItem}">${iconClock}<span>${escapeHtml(
        active
      )}</span></span>`
    : '';

  return `<div class="${styles.statsRow}">${ratingPart}${countBadge}${activePart}</div>`;
}

/**
 * @param {unknown[]} items
 * @param {string} className
 * @param {string} itemClass
 * @param {number} maxVisible
 * @param {{ wrap: string; tooltip: string; ariaNoun: string }|null} [pillMore]
 */
function pillRow(items, className, itemClass, maxVisible, pillMore = null) {
  const list = Array.isArray(items) ? items.filter(Boolean).slice(0) : [];
  if (!list.length) {
    return '';
  }
  const labels = [];
  for (const raw of list) {
    const label = typeof raw === 'string' ? raw : raw && raw.name;
    if (!label) {
      continue;
    }
    const trimmed = String(label).trim();
    if (trimmed) {
      labels.push(trimmed);
    }
  }
  if (!labels.length) {
    return '';
  }

  const limit =
    typeof maxVisible === 'number' &&
    Number.isFinite(maxVisible) &&
    maxVisible >= 0
      ? maxVisible
      : labels.length;
  const shown = labels.slice(0, limit);
  const overflow = labels.length > limit ? labels.length - limit : 0;

  const parts = shown.map(
    (label) => `<span class="${itemClass}">${escapeHtml(label)}</span>`
  );
  if (overflow > 0) {
    const rest = labels.slice(limit);
    const noun = pillMore && pillMore.ariaNoun ? pillMore.ariaNoun : 'items';
    const ariaFull = `${overflow} more ${noun}: ${rest.join(', ')}`;
    if (pillMore && pillMore.wrap && pillMore.tooltip) {
      const tooltipBody = rest
        .map(
          (label) => `<span class="${itemClass}">${escapeHtml(label)}</span>`
        )
        .join('');
      parts.push(
        `<span class="${itemClass} ${
          pillMore.wrap
        }" data-browse-pill-more="" aria-label="${escapeHtml(
          ariaFull
        )}">+${overflow}<span class="${
          pillMore.tooltip
        }" aria-hidden="true">${tooltipBody}</span></span>`
      );
    } else {
      parts.push(
        `<span class="${itemClass}" aria-label="${escapeHtml(
          ariaFull
        )}">+${overflow}</span>`
      );
    }
  }
  return `<div class="${className}">${parts.join('')}</div>`;
}

function renderAvatar(hit) {
  const name = hit.displayname || 'Expert';
  const url = (hit.avatar_url && String(hit.avatar_url).trim()) || '';
  if (url) {
    return `<img class="${styles.avatarImg}" src="${escapeHtml(
      url
    )}" alt="${escapeHtml(name)}" width="64" height="64" loading="lazy" />`;
  }
  return `<div class="${styles.avatarInitials}" aria-hidden="true">${escapeHtml(
    initialsFromDisplayName(hit.displayname)
  )}</div>`;
}

/**
 * HTML string for a Typesense `users_search` hit (or compatible shape). Uses CSS module classes
 * from UserBrowseCard.module.scss — safe for InstantSearch `templates.item`.
 *
 * @param {Record<string, unknown>} hit
 * @param {{
 *   viewerUserId?: string|number|null;
 *   portfolio?: Record<string, { isInTalentPool?: boolean }>;
 *   viewerUsername?: string|null;
 * }} [options]
 * @returns {string}
 */
export function renderUserBrowseCardMarkup(hit, options = {}) {
  const {
    viewerUserId = null,
    portfolio = {},
    viewerUsername = null,
  } = options;
  const name = escapeHtml(hit.displayname || 'No name');
  const profileHrefRaw = profilePath(hit.username);
  const profileHrefAttr = escapeHtml(profileHrefRaw);
  const cardAriaLabel = escapeHtml(
    `View profile: ${hit.displayname || 'Expert'}`
  );
  const loc = locationText(hit);
  const locHtml = loc
    ? `<div class="${styles.locationRow}">${iconPin}<span>${escapeHtml(
        loc
      )}</span></div>`
    : '';

  const statsHtml = statsRowHtml(hit);

  const statusBadges = renderToStaticMarkup(
    <UserStatusBadges
      user={{
        pro: Boolean(hit.pro),
        verified: Boolean(hit.verified),
        twine_recommends: Boolean(hit.twine_recommends),
      }}
      className={styles.statusBadges}
    />
  );

  const rolesPillRow = pillRow(
    Array.isArray(hit.roles) ? hit.roles : [],
    styles.pillsRow,
    styles.pillPrimary,
    MAX_ROLES,
    {
      wrap: styles.pillMoreWrap,
      tooltip: styles.pillMoreTooltip,
      ariaNoun: 'roles',
    }
  );
  const skillsPillRow = pillRow(
    Array.isArray(hit.skills) ? hit.skills : [],
    styles.pillsRow,
    styles.pillSecondary,
    maxSkillsForViewport(),
    {
      wrap: styles.pillMoreWrap,
      tooltip: styles.pillMoreTooltip,
      ariaNoun: 'skills',
    }
  );

  const rawBio = hit.bio && String(hit.bio).trim();
  const bioHtml = rawBio
    ? `<div class="${styles.bioRow}"><span class="${
        styles.bioText
      }">${escapeHtml(rawBio)}</span><span class="${
        styles.moreInfo
      }">more info &gt;</span></div>`
    : '';

  const usernameRaw = hit.username != null ? String(hit.username).trim() : '';
  const shortlistBtn =
    usernameRaw === ''
      ? ''
      : renderToStaticMarkup(
          <TalentPoolHeart
            browseUsername={usernameRaw}
            shortlistState={talentPoolDisplayForHit(
              hit,
              viewerUserId,
              portfolio
            )}
            iconSize={24}
          />
        );

  const viewerLoggedIn =
    viewerUsername != null && String(viewerUsername).trim() !== '';
  let inviteBtnHtml = '';
  if (usernameRaw !== '') {
    const viewerKey = viewerLoggedIn ? String(viewerUsername).trim() : '';
    const isSelf = viewerKey !== '' && viewerKey === usernameRaw;
    const sellerOrUnknown =
      hit.type == null || hit.type === '' || hit.type === 'seller';
    const fid = freelancerIdFromHit(hit);
    if (!isSelf && sellerOrUnknown && fid !== '') {
      inviteBtnHtml = `<span data-browse-invite-react="" data-username="${escapeHtml(
        usernameRaw
      )}" data-user-id="${escapeHtml(fid)}"></span>`;
    }
  }

  const musicProfile = browseHitHasMusicalRole(hit);
  const browseProjects = browseProjectsFromHit(hit, {
    musicLayout: musicProfile,
  });
  const projectsHtml =
    browseProjects.length > 0
      ? renderToStaticMarkup(
          <UserBrowseProjectsList
            projects={browseProjects}
            variant={musicProfile ? 'music' : 'default'}
          />
        )
      : '';

  const browseWorkExperience = browseWorkExperienceFromHit(hit);
  const workExperienceHtml =
    browseProjects.length === 0 && browseWorkExperience.length > 0
      ? renderToStaticMarkup(
          <UserBrowseWorkExperienceList items={browseWorkExperience} />
        )
      : '';

  return `
    <div class="${styles.hitCard}">
      <a class="${
        styles.hitCardHitArea
      }" href="${profileHrefAttr}" aria-label="${cardAriaLabel}"></a>
      <div class="${styles.hitCardContent}">
        <div class="${styles.hitCardTopRow}">
          <div class="${styles.avatarWrap}">${renderAvatar(hit)}</div>
          <div class="${styles.hitCardBody}">
            <div class="${styles.identityColumn}">
              <div class="${styles.nameRow}">
                <h2 class="${styles.displayName}">${name}</h2>
                ${statusBadges}
              </div>
              ${locHtml}
              ${statsHtml}
            </div>
          </div>
          <div class="${styles.viewProfileRow}">
            ${shortlistBtn}
            <span class="${styles.viewProfileBtn}">View profile</span>
            ${inviteBtnHtml}
          </div>
        </div>
        <div class="${styles.hitCardFullWidth}">
          ${rolesPillRow}
          ${skillsPillRow}
          ${bioHtml}
          ${projectsHtml}
          ${workExperienceHtml}
        </div>
      </div>
    </div>
  `;
}
